import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV, ASSETS, BRAND } from "@/lib/brand";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    const id = window.requestAnimationFrame(onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const darkHeroRoutes = ["/", "/franchise-opportunity"];
  const transparent = !scrolled && darkHeroRoutes.includes(location.pathname);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-pe-cream/90 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_24px_-12px_rgba(26,35,49,0.12)]"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14 flex items-center justify-between gap-6 h-[88px] md:h-[120px]">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
          <img
            src={transparent ? ASSETS.logo_header_light : ASSETS.logo_header}
            alt="Pizza Excellente logo"
            className={`h-12 md:h-16 w-auto object-contain transition-all duration-300 ${
              transparent ? "drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]" : ""
            }`}
          />
          <span className="sr-only">{BRAND.name}</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-10 flex-1 justify-end" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === "/"}
              data-testid={`nav-link-${item.href.replace(/\W+/g, "-").replace(/^-|-$/g, "") || "home"}`}
              className={({ isActive }) => {
                const base = "text-[14px] tracking-[0.12em] font-semibold uppercase transition-colors whitespace-nowrap";
                if (transparent) {
                  return `${base} drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)] ${
                    isActive ? "text-pe-rose-soft" : "text-white hover:text-pe-rose-soft"
                  }`;
                }
                return `${base} ${
                  isActive ? "text-pe-aubergine" : "text-pe-ink/80 hover:text-pe-aubergine"
                }`;
              }}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`xl:hidden p-2 transition-colors shrink-0 ${
            transparent ? "text-white hover:text-pe-rose-soft" : "text-pe-ink hover:text-pe-aubergine"
          }`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-pe-cream border-t border-black/5">
          <nav className="px-5 py-6 flex flex-col gap-1" aria-label="Mobile">
            {NAV.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === "/"}
                data-testid={`mobile-nav-${item.href.replace(/\W+/g, "-").replace(/^-|-$/g, "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 px-2 text-[15px] font-medium border-b border-black/5 ${
                    isActive ? "text-pe-aubergine" : "text-pe-ink/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
